import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
    OrderRouteService,
    RouteSetSearchOutputItemModel,
    RouteSetSearchOutputModel,
    ServerResponse
} from '@spa/api';
import {
    RouteSetAssociationFormControls,
    RouteSetFormControlsRouteArraySelector,
    RouteSetFormControlsRouteIsRequired
} from 'appAssets/descriptors';
import {CheckboxControl} from 'appShared/classes';

import {OrderRoutesAssociationEnum} from '../../enums';
import {RequiredValidatorKey} from '@spa/validators';
import {Subscription} from 'rxjs';

import {ColorCodesEnum, ComponentTypesEnum} from 'appShared/enums';
import {RouteSetAssociationFormControlsInterface} from 'appAssets/descriptors';

export class RouteSetAssociationForm {
    private orderRouteService: OrderRouteService;
    private readonly objectId: number;
    private readonly associationType: OrderRoutesAssociationEnum;
    private readonly brokerId: number;
    private readonly currencyCode: string;
    private initialRouteSetIds: number[];
    public availableRouteSets: RouteSetSearchOutputModel;
    public formGroup: FormGroup;
    public controls: RouteSetAssociationFormControlsInterface;
    public routesErrors: string[];
    public subscriptions: Subscription[];
    public routeSetFormControlsRouteArraySelector: string;

    // tslint:disable-next-line:max-line-length
    constructor(
        orderRouteService: OrderRouteService,
        associationType: OrderRoutesAssociationEnum,
        objectId: number,
        availableRouteSets: RouteSetSearchOutputModel,
        brokerId: number = null,
        currencyCode: string = null
    ) {
        this.objectId = objectId;
        this.associationType = associationType;
        this.availableRouteSets = availableRouteSets;
        this.orderRouteService = orderRouteService;
        this.subscriptions = [];
        this.routesErrors = [];
        this.routeSetFormControlsRouteArraySelector = RouteSetFormControlsRouteArraySelector;
        this.brokerId = brokerId;
        this.currencyCode = currencyCode;
        this.initialRouteSetIds = [];

    }

    private getSelectedRouteSetIds(): number[] {
        const routesFormControl = this.formGroup.get(this.routeSetFormControlsRouteArraySelector) as FormArray;

        return routesFormControl.value.filter((item: number) => item !== null);
    }

    private updateDirtyState(): void {
        if (this.hasChanges()) {
            this.formGroup.markAsDirty();

            return;
        }

        this.formGroup.markAsPristine();
    }

    private normalizeIds(ids: number[] = []): number[] {
        return ids.slice().sort((a: number, b: number) => a - b);
    }

    private areSameIds(first: number[], second: number[]): boolean {
        const normalizedFirst = this.normalizeIds(first);
        const normalizedSecond = this.normalizeIds(second);

        if (normalizedFirst.length !== normalizedSecond.length) {
            return false;
        }

        return normalizedFirst.every((id: number, index: number) => id === normalizedSecond[index]);
    }

    initForm(currentRouteSets: number[] = []): void {
        this.formGroup = new FormGroup({});
        this.initialRouteSetIds = this.normalizeIds(currentRouteSets);

        this.controls = RouteSetAssociationFormControls();
        const routeIdsFormArray = new FormArray([]);

        if (this.availableRouteSets && this.availableRouteSets.total_elements > 0) {
            this.controls.routeSets = this.availableRouteSets.content.map((r: RouteSetSearchOutputItemModel, index: number) => {
                const checkControl = new CheckboxControl({
                    name: index + '',
                    value: r.getId(),
                    placeholder: r.getName(),
                    nonCapitalizedPlaceholder: true,
                    validators: [],
                    componentType: ComponentTypesEnum.Checkbox,
                    checked: this.initialRouteSetIds.indexOf(r.getId()) > -1,
                    color: ColorCodesEnum.Primary,
                    expectBooleanValue: false
                });
                const checkboxFormControl = new FormControl(checkControl.value, checkControl.validators);
                routeIdsFormArray.push(checkboxFormControl);

                return checkControl;
            });
        }
        this.formGroup.addControl(this.routeSetFormControlsRouteArraySelector, routeIdsFormArray);
        this.subscriptions.push(routeIdsFormArray.valueChanges.subscribe(() => this.updateDirtyState()));
    }

    createRequest(): ServerResponse<null> {
        if (!this.hasChanges()) {
            return null;
        }
        const routeIds = this.getSelectedRouteSetIds();
        switch (this.associationType){
            case OrderRoutesAssociationEnum.Account:
                return this.orderRouteService.addRouteSetsToAccount(this.objectId, routeIds);
            case OrderRoutesAssociationEnum.AccountGroup:
                return this.orderRouteService.addRouteSetsToAccountGroup(this.objectId, routeIds, this.brokerId, this.currencyCode);
            case OrderRoutesAssociationEnum.TradingPlan:
                return this.orderRouteService.addRouteSetsToPlan(this.objectId, routeIds);
        }
    }

    hasChanges(): boolean {
        return !this.areSameIds(this.initialRouteSetIds, this.getSelectedRouteSetIds());
    }

    validateForm(messagesFunction: (controlsLabel?: string) => { [key: string]: string }): boolean {
        let isInvalidForm = false;

        const routeIds = this.getSelectedRouteSetIds();
        this.routesErrors.splice(0, this.routesErrors.length);
        if (routeIds.length === 0) {
            this.routesErrors.push(messagesFunction(RouteSetFormControlsRouteIsRequired)[RequiredValidatorKey]);

            isInvalidForm = true;
        }

        return !isInvalidForm;
    }
}
